import { Injectable, Renderer2, inject, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';

export type AriaLiveMode = 'polite' | 'assertive' | 'off';

/**
 * Service for managing accessibility features across the application
 */
@Injectable({
  providedIn: 'root'
})
export class AccessibilityService {
  private readonly renderer: Renderer2 = inject(RendererFactory2).createRenderer(null, null);
  private readonly document = inject(DOCUMENT);
  private liveRegion: HTMLElement | null = null;
  private announceTimer: ReturnType<typeof setTimeout> | null = null;
  private clearTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.createLiveRegion();
  }

  /**
   * Announce a message to screen readers
   * @param message The message to announce
   * @param priority The priority level (polite or assertive)
   * @param delay Optional delay before announcing (in milliseconds)
   */
  announce(message: string, priority: AriaLiveMode = 'polite', delay: number = 0): void {
    if (!message.trim()) {return;}

    // Cancel any pending announcement/clear to prevent timer interleaving
    if (this.announceTimer !== null) { clearTimeout(this.announceTimer); }
    if (this.clearTimer !== null) { clearTimeout(this.clearTimer); }

    this.announceTimer = setTimeout(() => {
      this.announceTimer = null;
      if (this.liveRegion) {
        this.renderer.setAttribute(this.liveRegion, 'aria-live', priority);
        this.renderer.setProperty(this.liveRegion, 'textContent', message);

        // Clear the message after a short delay to avoid repetition
        this.clearTimer = setTimeout(() => {
          this.clearTimer = null;
          if (this.liveRegion) {
            this.renderer.setProperty(this.liveRegion, 'textContent', '');
          }
        }, 1000);
      }
    }, delay);
  }

  /**
   * Announce loading state
   * @param resourceName The name of the resource being loaded
   */
  announceLoading(resourceName: string): void {
    this.announce(`Loading ${resourceName}...`, 'polite');
  }

  /**
   * Announce completion of loading
   * @param resourceName The name of the resource that finished loading
   * @param count Optional count of items loaded
   */
  announceLoadingComplete(resourceName: string, count?: number): void {
    const message = count !== undefined 
      ? `${resourceName} loaded. ${count} items available.`
      : `${resourceName} loaded.`;
    this.announce(message, 'polite');
  }

  /**
   * Announce an error
   * @param error The error message
   */
  announceError(error: string): void {
    this.announce(`Error: ${error}`, 'assertive');
  }

  /**
   * Announce a successful action
   * @param action The action that was completed
   */
  announceSuccess(action: string): void {
    this.announce(`Success: ${action}`, 'polite');
  }

  /**
   * Announce navigation changes
   * @param pageName The name of the new page
   */
  announceNavigation(pageName: string): void {
    this.announce(`Navigated to ${pageName}`, 'polite', 500);
  }

  /**
   * Announce search results
   * @param count Number of results found
   * @param query The search query
   */
  announceSearchResults(count: number, query?: string): void {
    const queryText = query ? ` for "${query}"` : '';
    const message = count === 0 
      ? `No results found${queryText}`
      : `${count} result${count !== 1 ? 's' : ''} found${queryText}`;
    this.announce(message, 'polite');
  }

  /**
   * Announce filter changes
   * @param filterName The name of the filter that changed
   * @param value The new filter value
   */
  announceFilterChange(filterName: string, value: string): void {
    this.announce(`${filterName} filter set to ${value}`, 'polite');
  }

  /**
   * Announce sort changes
   * @param field The field being sorted
   * @param direction The sort direction
   */
  announceSortChange(field: string, direction: 'ascending' | 'descending'): void {
    this.announce(`Table sorted by ${field} in ${direction} order`, 'polite');
  }

  /**
   * Focus management utility
   * @param selector CSS selector for the element to focus
   * @param delay Optional delay before focusing
   */
  focusElement(selector: string, delay: number = 0): void {
    setTimeout(() => {
      // GAP-FE-C014: legitimate — this is a shared service that focuses an
      // arbitrary caller-supplied selector anywhere in the app; it owns no
      // template, so ViewChild does not apply. Injected DOCUMENT is SSR-safe.
      const element = this.document.querySelector(selector) as HTMLElement;
      if (element) {
        // Make element focusable if it's not naturally focusable
        if (!this.isFocusable(element)) {
          this.renderer.setAttribute(element, 'tabindex', '-1');
        }
        element.focus();

        // Remove temporary tabindex after blur
        if (element.getAttribute('tabindex') === '-1') {
          this.renderer.listen(element, 'blur', () => {
            this.renderer.removeAttribute(element, 'tabindex');
          });
        }
      }
    }, delay);
  }

  /**
   * Check if an element is focusable
   * @param element The element to check
   */
  private isFocusable(element: HTMLElement): boolean {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ];

    return focusableSelectors.some(selector => element.matches(selector));
  }

  /**
   * Create the global live region for announcements
   */
  private createLiveRegion(): void {
    if (this.liveRegion) {return;}

    // Use Renderer2 for platform-agnostic DOM manipulation
    this.liveRegion = this.renderer.createElement('div');

    // Set ARIA attributes
    this.renderer.setAttribute(this.liveRegion, 'id', 'a11y-live-region');
    this.renderer.setAttribute(this.liveRegion, 'role', 'status');
    this.renderer.setAttribute(this.liveRegion, 'aria-live', 'polite');
    this.renderer.setAttribute(this.liveRegion, 'aria-atomic', 'true');
    this.renderer.setAttribute(this.liveRegion, 'aria-relevant', 'additions text');
    this.renderer.addClass(this.liveRegion, 'sr-only');

    // Append to body
    this.renderer.appendChild(this.document.body, this.liveRegion);
  }

  /**
   * Get keyboard navigation instructions for complex components
   * @param componentType The type of component
   */
  getKeyboardInstructions(componentType: 'table' | 'menu' | 'dialog' | 'form'): string {
    const instructions = {
      table: 'Use arrow keys to navigate cells, Enter to activate buttons, Space to select checkboxes',
      menu: 'Use arrow keys to navigate menu items, Enter to activate, Escape to close',
      dialog: 'Use Tab to navigate within dialog, Escape to close, focus is trapped within dialog',
      form: 'Use Tab to navigate between fields, Space to toggle checkboxes, Enter to submit'
    };

    return instructions[componentType] || '';
  }
}